import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

const pluginKey = new PluginKey<{ visible: boolean }>(
  "unsubscribe-footer-decoration",
);

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    unsubscribeFooterDecoration: {
      setUnsubscribeFooterVisible: (visible: boolean) => ReturnType;
    };
  }
}

/**
 * Preview-only decoration — shows what the unsubscribe footer will look like.
 * The actual footer is appended by Mailgun's native unsubscribe tracking
 * (`update_unsubscribe_tracking()` in `decoda/services/mailgun_domain.py`).
 *
 * Mailgun html_footer:
 *   <p style="text-align: center; font-size: 12px; color: #6b7280;">
 *     <a href="%unsubscribe_url%" style="color: #6b7280; text-decoration: underline;">
 *       Unsubscribe from marketing emails
 *     </a>
 *   </p>
 *
 * Mailgun text_footer:
 *   Unsubscribe: %unsubscribe_url%
 *
 * Keep the preview copy below in sync with the Mailgun footers above.
 */
export const UnsubscribeFooterDecoration = Extension.create<{
  initialVisible: boolean;
}>({
  name: "unsubscribeFooterDecoration",

  addOptions() {
    return { initialVisible: false };
  },

  addCommands() {
    return {
      setUnsubscribeFooterVisible:
        (visible: boolean) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(pluginKey, { visible });
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const { initialVisible } = this.options;
    return [
      new Plugin({
        key: pluginKey,
        state: {
          init() {
            return { visible: initialVisible };
          },
          apply(tr, value) {
            const meta = tr.getMeta(pluginKey);
            if (meta !== undefined) return meta;
            return value;
          },
        },
        props: {
          decorations(state) {
            const { visible } = pluginKey.getState(state)!;
            if (!visible) return DecorationSet.empty;

            const widget = Decoration.widget(
              state.doc.content.size,
              () => {
                const el = document.createElement("div");
                el.setAttribute("contenteditable", "false");
                el.style.cssText =
                  "margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;font-size:12px;color:#6b7280;pointer-events:none;user-select:none;";

                const p2 = document.createElement("p");
                p2.style.margin = "0";
                const link = document.createElement("span");
                link.style.cssText =
                  "color:#6b7280;text-decoration:underline;cursor:default;";
                link.textContent = "Unsubscribe from marketing emails";
                p2.appendChild(link);

                el.appendChild(p2);
                return el;
              },
              { side: 1, key: "unsubscribe-footer" },
            );
            return DecorationSet.create(state.doc, [widget]);
          },
        },
      }),
    ];
  },
});
